import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { ArrowBack, PhotoLibrary, Refresh } from '@mui/icons-material'
import { Alert, Box, Button, Card, CardContent, MenuItem, Skeleton, Stack, TextField, Typography } from '@mui/material'
import Player from '@vimeo/player'

import { FtueVideo, FtueVideoThumbnail } from '../../../api/core'
import FtueVideoPlayer from '../../../features/ftue-videos/components/FtueVideoPlayer/FtueVideoPlayer'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useGetFtueChapterTagsQuery, useGetFtueGameVersionsQuery, useGetFtueVideoQuery, useUpdateFtueVideoMutation } from '../../api/ftue-videos'
import { useGetInternalGameQuery } from '../../api/internal-games'
import { useLazyGetVimeoOEmbedQuery } from '../../api/vimeo'
import { DiscardChangesDialog } from '../../components/DiscardChangesDialog/DiscardChangesDialog'
import { FtueChapterEditor } from '../../components/FtueChapterEditor/FtueChapterEditor'
import { formatChapterTime } from '../../components/FtueChapterEditor/ftueChapterHelpers'
import { InputTranslator } from '../../components/InputTranslator/InputTranslator'
import { VimeoPicturePickerDialog } from '../../components/VimeoPicturePickerDialog/VimeoPicturePickerDialog'
import { FtueChapterTagsField } from '../../features/ftue-videos/components/FtueChapterTagsField/FtueChapterTagsField'
import { FtueGameHeader } from '../../features/ftue-videos/components/FtueGameHeader/FtueGameHeader'
import {
  formatReleaseDate,
  ftueVideoFieldLabelKeys,
  getFtueMarketOptions,
  getUnstoredFtueVideoFields,
  isVisibleToCustomers,
  toFtueVideoFormValues,
  toFtueVideoUpdateBody,
  withStoredVersion,
} from '../../features/ftue-videos/helpers/ftueVideoHelpers'
import { FtueVideoFormValues } from '../../features/ftue-videos/types/InternalFtueVideo'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'
import { getThumbnailPictureId, thumbnailsMatch, toVimeoThumbnails } from '../../helpers/vimeo'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { ftueVideosGamePath, ftueVideosPath } from './FtueVideosPage'

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const FtueVideoEditPage: FC = () => {
  const { videoId = '' } = useParams<{ videoId: string }>()
  usePage(pageService.getInternalPageWithId('internal-ftue-video-edit'))
  const { t } = useTranslation()
  const { notify, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const videoQuery = useGetFtueVideoQuery(videoId)
  const video = videoQuery.data
  const gameQuery = useGetInternalGameQuery(video?.gameId ?? '', { skip: !video?.gameId })
  const game = gameQuery.data
  const chapterTagsQuery = useGetFtueChapterTagsQuery()
  const [updateVideo, { isLoading: isSaving }] = useUpdateFtueVideoMutation()
  const [loadOEmbed, { isFetching: isLoadingThumbnail }] = useLazyGetVimeoOEmbedQuery()
  const [player, setPlayer] = useState<Player>()
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty },
  } = useForm<FtueVideoFormValues>({ defaultValues: toFtueVideoFormValues(undefined) })
  const values = useWatch({ control }) as FtueVideoFormValues
  const { guard, leave } = useEditPageLeave(isDirty)
  const loadedVideoId = useRef<string>()

  // Only on the first load of a video, so a refetch after saving can't replace edits that weren't stored.
  useEffect(() => {
    if (video && loadedVideoId.current !== video.id) {
      loadedVideoId.current = video.id
      reset(toFtueVideoFormValues(video))
    }
  }, [video, reset])

  const versionsQuery = useGetFtueGameVersionsQuery({ appId: game?.appId ?? 0, marketIso: values.marketIso }, { skip: !game?.appId || !values.marketIso })
  const isStoredMarket = !!video && values.marketIso === video.marketIso
  const versionOptions = useMemo(
    () =>
      withStoredVersion(
        versionsQuery.data ?? [],
        isStoredMarket && video ? { version: video.gameVersion, versionReleaseDate: video.gameVersionReleaseDate } : undefined
      ),
    [versionsQuery.data, isStoredMarket, video]
  )
  const marketOptions = useMemo(() => getFtueMarketOptions(game?.markets, video?.marketIso), [game?.markets, video?.marketIso])
  const chapterTags = useMemo(() => chapterTagsQuery.data ?? [], [chapterTagsQuery.data])

  const previewVideo = useMemo<FtueVideo | undefined>(
    () =>
      video
        ? {
            ...video,
            names: values.names,
            chapters: (values.chapters ?? []).map((chapter, i) => ({ ...chapter, chapterId: chapter.chapterId ?? -(i + 1) })),
          }
        : undefined,
    [video, values.names, values.chapters]
  )

  const changeMarket = (marketIso: string) => {
    setValue('marketIso', marketIso, { shouldDirty: true })
    const storedVersion = video && marketIso === video.marketIso
    setValue('gameVersion', storedVersion ? video.gameVersion : '', { shouldDirty: true })
    setValue('gameVersionReleaseDate', storedVersion ? video.gameVersionReleaseDate : 0, { shouldDirty: true })
  }

  const changeVersion = (version: string) => {
    const option = versionOptions.find((candidate) => candidate.version === version)
    if (!option) return
    setValue('gameVersion', option.version, { shouldDirty: true })
    setValue('gameVersionReleaseDate', option.versionReleaseDate, { shouldDirty: true })
  }

  const applyPickedPicture = (thumbnails: FtueVideoThumbnail[]) => {
    setIsPickerOpen(false)
    setValue('thumbnails', thumbnails, { shouldDirty: true })
  }

  const applyCurrentVimeoThumbnail = async () => {
    if (!video) return
    try {
      const oembed = await loadOEmbed(video.vimeoId).unwrap()
      const thumbnails = toVimeoThumbnails(oembed.thumbnailUrl, { keepQuery: true })
      if (thumbnailsMatch(thumbnails, values.thumbnails)) {
        notify(t('internal-analysts:ftue_videos_thumbnail_already_current'), 'info')
        return
      }
      setValue('thumbnails', thumbnails, { shouldDirty: true })
    } catch {
      notifyError(t('internal-analysts:ftue_videos_thumbnail_load_failed'))
    }
  }

  const listPath = video?.gameId ? ftueVideosGamePath(video.gameId) : ftueVideosPath
  const isMissingRequired = !values.names?.en?.trim() || !values.marketIso || !values.gameVersion

  const save = handleSubmit(async (formValues) => {
    if (!video || isMissingRequired) return
    const body = toFtueVideoUpdateBody(video, formValues)
    const name = formValues.names.en || '---'
    let stored: FtueVideo | null | undefined
    let failure: unknown
    try {
      stored = await updateVideo(body).unwrap()
    } catch (error) {
      failure = error
      stored = await videoQuery
        .refetch()
        .unwrap()
        .catch(() => undefined)
    }
    const unstored = stored ? getUnstoredFtueVideoFields(body, stored) : undefined
    if (unstored && unstored.length === 0) {
      notify(t(failure ? 'internal-analysts:ftue_videos_saved_unconfirmed' : 'internal-analysts:ftue_videos_saved', { name }), failure ? 'warning' : 'success')
      leave(listPath)
      return
    }
    if (failure) {
      const reason = getValidationErrorMessage(failure)
      notifyError(reason ? t('internal-analysts:ftue_videos_save_failed_reason', { reason }) : t('internal-analysts:ftue_videos_save_failed'))
      return
    }
    const fields = (unstored ?? []).map((field) => t(ftueVideoFieldLabelKeys[field])).join(', ')
    notifyError(t('internal-analysts:ftue_videos_not_stored', { fields }))
  })

  const thumbnail = values.thumbnails?.[3] ?? values.thumbnails?.[0]
  const thumbnailChanged = !!video && getThumbnailPictureId(values.thumbnails?.[0]?.link) !== getThumbnailPictureId(video.thumbnails?.[0]?.link)
  const isMissing = videoQuery.isSuccess && video === null
  const selectedVersion = versionOptions.find((option) => option.version === values.gameVersion)

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBack />} onClick={() => navigate(listPath)}>
            {t('internal-common:back')}
          </Button>
          <Typography variant="h5" fontWeight="bold">
            {t('internal-analysts:ftue_videos_edit_title', { name: video?.names?.en || '' })}
          </Typography>
        </Stack>
        <Button variant="contained" onClick={save} disabled={!video || isSaving || isMissingRequired}>
          {t('internal-common:save')}
        </Button>
      </Stack>

      {videoQuery.isLoading ? (
        <Skeleton variant="rectangular" height={480} />
      ) : isMissing ? (
        <Typography color="error.main">{t('internal-analysts:ftue_videos_missing')}</Typography>
      ) : videoQuery.isError || !video || !previewVideo ? (
        <Typography color="error.main">{t('internal-analysts:ftue_videos_load_one_failed')}</Typography>
      ) : (
        <Stack spacing={3}>
          {isVisibleToCustomers(video) ? (
            <Alert severity="info">{t('internal-analysts:ftue_videos_customer_note')}</Alert>
          ) : (
            <Alert severity="warning">{t('internal-analysts:ftue_videos_not_visible_note')}</Alert>
          )}
          {isDirty && <Alert severity="warning">{t('internal-analysts:ftue_videos_unsaved')}</Alert>}
          <Box display="grid" gridTemplateColumns={{ xs: 'minmax(0, 1fr)', md: 'minmax(0, 7fr) minmax(0, 5fr)' }} gap={3} alignItems="start">
            <Box>
              <FtueVideoPlayer video={previewVideo} hideChapterList hideCloseButton onPlayerReady={setPlayer} />
            </Box>
            <Box>
              <Card>
                <CardContent>
                  <Stack spacing={2}>
                    {gameQuery.isError ? (
                      <Typography color="error.main">{t('internal-analysts:ftue_videos_game_load_failed')}</Typography>
                    ) : (
                      <FtueGameHeader game={game} isLoading={gameQuery.isLoading} />
                    )}
                    <Controller
                      name="names"
                      control={control}
                      render={({ field }) => (
                        <InputTranslator
                          value={field.value}
                          onChange={field.onChange}
                          dialogTitle={t('internal-analysts:ftue_videos_name')}
                          label={t('internal-analysts:ftue_videos_name')}
                          disabled={isSaving}
                        />
                      )}
                    />
                    <TextField
                      select
                      size="small"
                      label={t('internal-analysts:ftue_videos_market')}
                      value={marketOptions.some((option) => option.iso === values.marketIso) ? values.marketIso : ''}
                      onChange={(event) => changeMarket(event.target.value)}
                      disabled={isSaving || !marketOptions.length}
                      fullWidth
                    >
                      {marketOptions.map((option) => (
                        <MenuItem key={option.iso} value={option.iso}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label={t('internal-analysts:ftue_videos_version')}
                      value={selectedVersion ? selectedVersion.version : ''}
                      onChange={(event) => changeVersion(event.target.value)}
                      disabled={isSaving || !versionOptions.length}
                      error={!values.gameVersion}
                      helperText={
                        versionsQuery.isFetching
                          ? t('internal-analysts:ftue_videos_versions_loading')
                          : versionsQuery.isError
                          ? t('internal-analysts:ftue_videos_versions_failed')
                          : !values.gameVersion
                          ? t('internal-analysts:ftue_videos_version_required')
                          : t('internal-analysts:ftue_videos_release_date_value', { date: formatReleaseDate(values.gameVersionReleaseDate) })
                      }
                      fullWidth
                    >
                      {versionOptions.map((option) => (
                        <MenuItem key={option.version} value={option.version}>
                          {t('internal-analysts:ftue_videos_version_option', { version: option.version, date: formatReleaseDate(option.versionReleaseDate) })}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Box>
                      <Typography variant="subtitle2" mb={1}>
                        {t('internal-analysts:ftue_videos_thumbnail')}
                      </Typography>
                      {thumbnail ? (
                        <Box component="img" src={thumbnail.link} alt={t('internal-analysts:ftue_videos_thumbnail')} sx={{ display: 'block', width: '100%' }} />
                      ) : (
                        <Typography color="text.secondary">---</Typography>
                      )}
                      {thumbnailChanged && (
                        <Typography variant="body2" color="warning.main" mt={1}>
                          {t('internal-analysts:ftue_videos_thumbnail_changed')}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={2} alignItems="center" mt={1} flexWrap="wrap">
                        <Button variant="outlined" startIcon={<Refresh />} onClick={applyCurrentVimeoThumbnail} disabled={isLoadingThumbnail || isSaving}>
                          {t('internal-analysts:ftue_videos_thumbnail_use_current')}
                        </Button>
                        <Button variant="outlined" startIcon={<PhotoLibrary />} onClick={() => setIsPickerOpen(true)} disabled={isSaving}>
                          {t('internal-analysts:ftue_videos_thumbnail_choose')}
                        </Button>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {t('internal-analysts:ftue_videos_thumbnail_help')}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Box>
          <Card>
            <CardContent>
              {chapterTagsQuery.isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t('internal-analysts:ftue_videos_chapter_tags_failed')}
                </Alert>
              )}
              <Controller
                name="chapters"
                control={control}
                render={({ field }) => (
                  <FtueChapterEditor
                    chapters={field.value}
                    onChange={field.onChange}
                    player={player}
                    disabled={isSaving}
                    renderChapterExtra={(chapter, index) => (
                      <FtueChapterTagsField
                        value={chapter.tags ?? []}
                        tags={chapterTags}
                        disabled={isSaving}
                        label={t('internal-analysts:ftue_videos_chapter_tags_at', { time: formatChapterTime(chapter.dataSeek) })}
                        onChange={(tags) => field.onChange(field.value.map((other, i) => (i === index ? { ...other, tags } : other)))}
                      />
                    )}
                  />
                )}
              />
            </CardContent>
          </Card>
        </Stack>
      )}

      {video && (
        <VimeoPicturePickerDialog
          open={isPickerOpen}
          vimeoId={video.vimeoId}
          currentThumbnails={values.thumbnails}
          onClose={() => setIsPickerOpen(false)}
          onSelect={applyPickedPicture}
        />
      )}
      <DiscardChangesDialog
        guard={guard}
        title={t('internal-analysts:ftue_videos_discard_title')}
        text={t('internal-analysts:ftue_videos_discard_confirm')}
        discardText={t('internal-analysts:ftue_videos_discard')}
      />
    </Box>
  )
}
